import { useState, useEffect } from 'react'

const RECENT_USERS_KEY = 'hubble_recent_visited_users'
const MAX_RECENT_USERS = 10

interface RecentUser {
  id: number
  name: string
  email: string
  timestamp: number
}

interface UseRecentUsersReturn {
  recentUsers: RecentUser[]
  addRecentUser: (user: { id: number; name: string; email: string }) => void
  clearRecentUsers: () => void
  removeRecentUser: (userId: number) => void
}

const useRecentUsers = (): UseRecentUsersReturn => {
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([])

  // Function to load from localStorage
  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(RECENT_USERS_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          setRecentUsers(parsed)
        }
      }
    } catch (error) {
      console.error('Error loading recent users:', error)
    }
  }

  // Load recent users from localStorage on mount and poll for changes
  useEffect(() => {
    loadFromLocalStorage()

    // Poll localStorage for changes every second
    const interval = setInterval(() => {
      loadFromLocalStorage()
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  // Save to localStorage whenever recentUsers changes
  useEffect(() => {
    try {
      localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(recentUsers))
    } catch (error) {
      console.error('Error saving recent users:', error)
    }
  }, [recentUsers])

  const addRecentUser = (user: { id: number; name: string; email: string }) => {
    if (!user.id) return

    setRecentUsers((prev) => {
      // Remove if already exists
      const filtered = prev.filter((u) => u.id !== user.id)
      // Add to beginning with timestamp
      const newEntry: RecentUser = {
        ...user,
        timestamp: Date.now(),
      }
      const updated = [newEntry, ...filtered]
      // Keep only MAX_RECENT_USERS items
      return updated.slice(0, MAX_RECENT_USERS)
    })
  }

  const clearRecentUsers = () => {
    setRecentUsers([])
  }

  const removeRecentUser = (userId: number) => {
    setRecentUsers((prev) => prev.filter((u) => u.id !== userId))
  }

  return {
    recentUsers,
    addRecentUser,
    clearRecentUsers,
    removeRecentUser,
  }
}

export default useRecentUsers
